import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * useAnchoredLayer — a fixed-position layer parked against an anchor element.
 *
 * The shared half of `ActionMenu` and `ColumnFilter`: both put a panel in a
 * portal on `document.body` (so no ancestor's `overflow: hidden` can clip it —
 * every card in this app has one, `AppDataTable`'s included) and both then have
 * to answer the same three questions. Where does it go, what happens when it
 * would fall off the bottom of the window, and what closes it.
 *
 * Written once because the second copy is where they drift: a menu that flips
 * above the trigger near the foot of a long table and a filter panel that does
 * not is one component behaving two ways.
 *
 * ── Placement is ONE pass, in a layout effect ────────────────────────────────
 * The layer's height decides whether it fits below the anchor, and a height is
 * only knowable once it is in the DOM. It already is: the caller renders the
 * portal in the same commit that `open` becomes true, so a `useLayoutEffect`
 * measures it and places it before the browser paints.
 *
 * ⚠️ It was briefly two passes — place blind in a `useEffect`, then measure and
 * correct in a `useLayoutEffect` — and that is a trap worth naming, because the
 * layout effect runs FIRST in a commit and the plain effect then overwrote its
 * answer. With `[open, ready]` deps the two settled on identical dependency
 * values across consecutive commits, so the correcting pass never ran again and
 * the layer stayed `visibility: hidden` for ever: open, mounted, measurable,
 * invisible. One pass cannot get out of step with itself.
 *
 * Usage:
 *   const { anchorRef, layerRef, style } = useAnchoredLayer({
 *     open, onClose, width: 180,
 *   });
 *
 *   <button ref={anchorRef} onClick={…} />
 *   {open && createPortal(<div ref={layerRef} style={style}>…</div>, document.body)}
 *
 * Options:
 *   open      — whether the layer is mounted
 *   onClose   — called on a click outside the anchor and the layer, and on Escape
 *   width     — the layer's width in px. ⚠️ Must match what the layer actually
 *               renders: it right-aligns the panel to the anchor, and a layer
 *               wider than this number hangs that much further right.
 *   gap       — px between anchor and layer (default 4)
 *   margin    — px kept clear of the viewport edges (default 8)
 */
const useAnchoredLayer = ({ open, onClose, width, gap = 4, margin = 8 }) => {
  // `ready: false` — mounted but not yet measured, so rendered hidden rather
  // than flashing at the top-left corner.
  const [coords, setCoords] = useState({ top: 0, left: 0, ready: false });
  const anchorRef = useRef(null);
  const layerRef = useRef(null);

  // A ref, not a dep: `onClose` is nearly always an inline arrow, and putting it
  // in the effect's deps would tear down and rebuild the listeners on every
  // render of the host component.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  const place = useCallback(() => {
    if (!anchorRef.current || !layerRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const height = layerRef.current.offsetHeight;
    // Right-aligned to the anchor, then clamped inside the viewport.
    const left = Math.max(
      margin,
      Math.min(rect.right - width, window.innerWidth - width - margin)
    );
    const up = window.innerHeight - rect.bottom < height + margin;
    setCoords({
      top: up ? rect.top - height - gap : rect.bottom + gap,
      left,
      ready: true,
    });
  }, [width, gap, margin]);

  useLayoutEffect(() => {
    if (open) {
      place();
      return;
    }
    // Back to unmeasured, so the next open does not paint one frame at the
    // previous anchor's position. Guarded so closing costs no extra render.
    setCoords((c) => (c.ready ? { ...c, ready: false } : c));
  }, [open, place]);

  // Follow the anchor while open. `true` on the scroll listener is the capture
  // phase — without it a scroll inside the table's own overflow container never
  // reaches window and the layer stays behind.
  useEffect(() => {
    if (!open) return;
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, place]);

  // Click-outside and Escape. `mousedown`, not `click`: a control inside the
  // layer that unmounts on mouseup would otherwise leave the click landing on
  // nothing and read as "outside".
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      if (anchorRef.current?.contains(e.target)) return;
      if (layerRef.current?.contains(e.target)) return;
      onCloseRef.current?.();
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") onCloseRef.current?.();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return {
    anchorRef,
    layerRef,
    style: {
      position: "fixed",
      top: coords.top,
      left: coords.left,
      // --z-popover (1070), the step for an anchored layer that must clear a
      // dialog: this hook portals a menu out of whatever opened it, and that is
      // very often a modal. It read 9999 before the ladder existed — a number
      // that was only ever "higher than the last thing this collided with".
      // See styles/tokens/_layer.scss.
      zIndex: "var(--z-popover)",
      visibility: coords.ready ? "visible" : "hidden",
    },
  };
};

export default useAnchoredLayer;
